import {useCallback, useMemo} from "react";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {ViewType} from "~/components/ottopilotDataViewer/view";
import type {ViewMode} from "~/components/viewModeSegmentedControl";

const VIEW_MODES: Array<ViewMode> = ["table", "kanban", "list"];

export function useDataViewerViewModeControl<T extends {id: Uuid}>(controller: OttopilotDataViewerController<T>) {
    const viewIdByMode = useMemo(() => {
        const result: Partial<Record<ViewMode, Uuid>> = {};
        for (const view of controller.views) {
            if (view.viewType === ViewType.table) {
                result.table = view.id;
            }
            if (view.viewType === ViewType.kanbanBoard) {
                result.kanban = view.id;
            }
            if (view.viewType === ViewType.list) {
                result.list = view.id;
            }
        }
        return result;
    }, [controller.views]);

    const selectedViewMode = useMemo((): ViewMode => {
        const currentView = controller.views.find((view) => view.id === controller.currentViewId);
        if (currentView?.viewType === ViewType.kanbanBoard) {
            return "kanban";
        }
        if (currentView?.viewType === ViewType.list) {
            return "list";
        }
        return "table";
    }, [controller.currentViewId, controller.views]);

    const onViewModeChange = useCallback(
        (mode: ViewMode) => {
            const viewId = viewIdByMode[mode];
            if (viewId !== undefined) {
                controller.setExplicitViewId(viewId);
            }
        },
        [controller, viewIdByMode],
    );

    const availableViewModes = useMemo(
        () => VIEW_MODES.filter((mode) => viewIdByMode[mode] !== undefined),
        [viewIdByMode],
    );

    return {
        selectedViewMode,
        onViewModeChange,
        availableViewModes,
    };
}
